import type { CompositePrimaryKey } from '#layers/data-model/types/schema/primary-key';
import type { ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import type { QueryCapability } from './query-capability';
import type { QuerySchemaDelete } from './query-schema';
import { FilterOperator, LogicalOperator } from '#layers/data-model/types/filters';
import { buildFilterFromItemKeys } from '#layers/data-model/utils/filter-composition';
import { getCollectionPrimaryKeyFields } from './collection';
import { queryCapabilityAllows } from './query-capability';

/** A selected-row delete exists only when the operation accepts the key filter the transport builds. */
export class SelectedItemDelete {
	private constructor(private readonly fields: ResolvedMonospacePrimitiveField[]) {}

	static resolve(collection: ResolvedMonospaceCollection, capability: QueryCapability<QuerySchemaDelete>): SelectedItemDelete | undefined {
		const fields = getCollectionPrimaryKeyFields(collection);
		if (fields.length === 0) return;
		const accepted = queryCapabilityAllows(capability, (operation) => {
			const filter = operation.filter();
			if (!filter) return false;
			if (fields.length === 1) return filter.primitiveFieldById(fields[0]!.id)?.operators().includes(FilterOperator.IN) === true;
			return filter.logical(LogicalOperator.OR)
				&& fields.every((field) => filter.primitiveFieldById(field.id)?.operators().includes(FilterOperator.EQ));
		});
		return accepted ? new SelectedItemDelete(fields) : undefined;
	}

	buildFilter(keys: readonly CompositePrimaryKey[]) {
		if (keys.length === 0 || keys.some((key) => this.fields.some((field) => key[field.apiName] == null))) return;
		return buildFilterFromItemKeys({ keys: [...keys], primaryKeyFields: this.fields });
	}
}
